import React, { useState, useEffect } from 'react';
import { suppliersAPI, SupplierProductCategory } from '../../services/api';
import { Card, CardBody, Button, Spinner, EmptyState, Badge, Input, Modal } from '../ui';
import { PlusIcon, PencilIcon, TrashIcon, TagIcon } from '@heroicons/react/24/outline';

const SupplierCategoriesSection: React.FC = () => {
  const [categories, setCategories] = useState<SupplierProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<SupplierProductCategory | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  // Delete state
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await suppliersAPI.getCategories();
      setCategories(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleOpenModal = (category?: SupplierProductCategory) => {
    if (category) {
      setEditingCategory(category);
      setFormData({ name: category.name, description: category.description || '' });
    } else {
      setEditingCategory(null);
      setFormData({ name: '', description: '' });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
    setFormData({ name: '', description: '' });
    setError('');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      if (editingCategory) {
        await suppliersAPI.updateCategory(editingCategory.id, formData);
      } else {
        await suppliersAPI.createCategory(formData);
      }
      await fetchCategories();
      handleCloseModal();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setDeleting(true);
      setError('');
      await suppliersAPI.deleteCategory(id);
      await fetchCategories();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete category');
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  return (
    <div className="space-y-4">
      {error && !isModalOpen && (
        <Card variant="bordered" className="border-error-200 bg-error-50">
          <CardBody className="p-4">
            <div className="text-sm text-error-700">{error}</div>
          </CardBody>
        </Card>
      )}

      <div className="flex justify-end">
        <Button variant="primary" leftIcon={<PlusIcon className="h-5 w-5" />} onClick={() => handleOpenModal()}>
          Add Category
        </Button>
      </div>

      <Card variant="elevated" className="overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-40"><Spinner size="lg" /></div>
        ) : categories.length === 0 ? (
          <EmptyState
            icon={<TagIcon className="h-12 w-12" />}
            title="No categories found"
            description="Add your first supplier product category to get started."
            action={
              <Button variant="primary" onClick={() => handleOpenModal()}>
                Add Category
              </Button>
            }
          />
        ) : (
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {categories.map((cat) => (
                <tr key={cat.id} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">{cat.name}</td>
                  <td className="px-6 py-4 text-sm text-neutral-500">{cat.description || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant={cat.isActive ? 'success' : 'neutral'} size="sm">
                      {cat.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                       <button onClick={() => handleOpenModal(cat)} className="text-primary-600 hover:text-primary-900 transition-colors">
                         <PencilIcon className="h-5 w-5" />
                       </button>
                       {deleteId === cat.id ? (
                         <div className="flex items-center space-x-2">
                           <button onClick={() => handleDelete(cat.id)} disabled={deleting} className="text-error-600 hover:text-error-900 font-medium transition-colors">
                             {deleting ? '...' : 'Confirm'}
                           </button>
                           <button onClick={() => setDeleteId(null)} disabled={deleting} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                             Cancel
                           </button>
                         </div>
                       ) : (
                         <button onClick={() => setDeleteId(cat.id)} className="text-error-600 hover:text-error-900 transition-colors">
                           <TrashIcon className="h-5 w-5" />
                         </button>
                       )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={editingCategory ? 'Edit Category' : 'Add Category'} size="md">
        <form onSubmit={handleSave} className="space-y-4">
          {error && isModalOpen && (
            <div className="text-sm text-error-700 bg-error-50 p-3 rounded-md border border-error-200">{error}</div>
          )}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Name</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g. Essential Oils"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional description"
              rows={3}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-200">
            <Button variant="outline" onClick={handleCloseModal} type="button">Cancel</Button>
            <Button variant="primary" type="submit" isLoading={saving}>Save</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SupplierCategoriesSection;
